'use client';

import type { ComponentType } from 'react';
import {
  Upload,
  ListFilter,
  Boxes,
  Cable,
  AlertTriangle,
  FileText,
  ClipboardList,
  Box,
  Building2,
  Package,
} from 'lucide-react';

/**
 * App-scoped left-nav descriptors (spec: app-scoped-left-nav).
 *
 * When the user is inside an app, the shell replaces the global icon rail with
 * an app-specific left menu rendered purely from the app's descriptor here —
 * there is no per-app branching in the shell. Each item is either an **in-app
 * section** (`sectionKey`, stays inside the app shell) or an **external
 * cross-link** (`href`, leaves the app). Per-item visibility is dynamic
 * (per-party permissions) and published by the app to `appNavSlice` — the
 * descriptor only declares the superset of items + their order/grouping.
 *
 * Label keys resolve under the `appNav` i18n namespace.
 */
export interface AppNavItem {
  /** i18n key under the `appNav` namespace. */
  labelKey: string;
  icon?: ComponentType<{ className?: string }>;
  /** In-app section ('' = default). Mutually exclusive with `href`. */
  sectionKey?: string;
  /** External workspace route (full path). Mutually exclusive with `sectionKey`. */
  href?: string;
}

export interface AppNavGroup {
  /** Optional group header i18n key (e.g. Features / Master Data). */
  titleKey?: string;
  items: AppNavItem[];
}

export interface AppNavDescriptor {
  /** Registry app key, e.g. `apps/pack-analysis`. */
  appKey: string;
  /** App display-name i18n key under `appNav`. */
  titleKey: string;
  icon?: ComponentType<{ className?: string }>;
  /** Section rendered when the URL has no section segment. */
  defaultSection: string;
  groups: AppNavGroup[];
}

const packAnalysisDescriptor: AppNavDescriptor = {
  appKey: 'apps/pack-analysis',
  titleKey: 'packAnalysis.title',
  icon: Boxes,
  defaultSection: 'ingest',
  groups: [
    {
      items: [
        { labelKey: 'packAnalysis.sections.ingest', icon: Upload, sectionKey: 'ingest' },
        { labelKey: 'packAnalysis.sections.browser', icon: ListFilter, sectionKey: 'browser' },
        { labelKey: 'packAnalysis.sections.rejected', icon: AlertTriangle, sectionKey: 'rejected' },
        { labelKey: 'packAnalysis.sections.packPlanner', icon: Boxes, sectionKey: 'pack-planner' },
        { labelKey: 'packAnalysis.sections.bridge', icon: Cable, sectionKey: 'mlinkone-bridge' },
      ],
    },
  ],
};

const mlinkoneDescriptor: AppNavDescriptor = {
  appKey: 'apps/mlinkone',
  titleKey: 'mlinkone.title',
  icon: Package,
  defaultSection: 'ingest',
  groups: [
    {
      // All PO-information surfaces live under one umbrella. Import / PO Documents
      // / Rejected are the admin import pipeline; PO List is the end-user PO view
      // (By PO / By Shipment).
      titleKey: 'groups.purchaseOrders',
      items: [
        { labelKey: 'mlinkone.sections.ingest', icon: Upload, sectionKey: 'ingest' },
        { labelKey: 'mlinkone.sections.poDocuments', icon: FileText, sectionKey: 'po-documents' },
        { labelKey: 'mlinkone.sections.rejected', icon: AlertTriangle, sectionKey: 'rejected' },
        { labelKey: 'mlinkone.sections.poList', icon: ClipboardList, sectionKey: 'po-list' },
      ],
    },
    {
      // The packing lifecycle (mlinkone-create-packing-lifecycle). Packing Lists (read-only
      // browser) lands first; Labelling / ASN / Booking join here as they are built.
      titleKey: 'groups.packing',
      items: [
        { labelKey: 'mlinkone.sections.packingLists', icon: Boxes, sectionKey: 'packing-lists' },
      ],
    },
    {
      // Supplier self-service settings (mirrors ML1 1.0's "Settings" section that
      // held Company Profile). The deferred Labelling Delivery + ASN-Info config
      // will land here too.
      titleKey: 'groups.settings',
      items: [
        { labelKey: 'mlinkone.sections.companyProfile', icon: Building2, sectionKey: 'settings/company-profile' },
      ],
    },
    {
      titleKey: 'groups.masterData',
      items: [
        { labelKey: 'mlinkone.sections.itemMaster', icon: Boxes, sectionKey: 'master-data/item-master' },
        { labelKey: 'mlinkone.sections.cartonTypes', icon: Box, sectionKey: 'master-data/carton-types' },
      ],
    },
  ],
};

export const appNavRegistry: Record<string, AppNavDescriptor> = {
  [packAnalysisDescriptor.appKey]: packAnalysisDescriptor,
  [mlinkoneDescriptor.appKey]: mlinkoneDescriptor,
};

/** Resolve the descriptor for an app key, or null. */
export function resolveAppNav(appKey: string | null): AppNavDescriptor | null {
  return appKey ? appNavRegistry[appKey] ?? null : null;
}
